import { Add, ArrowDropDown, Edit, Info, Lock, Save } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import _ from "lodash";
import { ChangeEvent, KeyboardEvent, useMemo, useState } from "react";
import { FormError } from "../../App/Components/Forms/formHelpers";
import Modal from "../../App/Components/Modal/Modal";
import { BaseFormField, ICampaignFormField, ICampaignFormState } from "../../App/Typings/Campaigns";
import { IFeatureToggles } from "../../App/Typings/FeatureToggles";
import { FieldModalSchema, maxFieldLabelLength } from "../Schema/CampaignFormSchema";
import { DraggableDropdownOptions } from "./DraggableDropdownOptions";
import { JobAlertsAdGroupDisplay } from "./JobAlertsAdGroupDisplay";
import { presetFields } from "./PresetFields";

const fieldOptions: { [key: string]: string } = {
  dropdown: "Dropdown",
  multiSelect: "Multi Select Dropdown",
  text: "Text Input",
  upload: "File Upload",
  interestPicker: "Interest Picker",
  toggle: "Toggle",
  email: "Email Input",
  phone: "Phone Input",
};

const lockedFields = [
  "firstName",
  "lastName",
  "contactPreference",
  "uploadResume",
  "areaOfInterest",
  "interestPicker",
  "email",
  "phone",
  "optIn",
];

export default function FieldEditModal({
  action,
  fieldToEdit = "",
}: {
  action: string;
  fieldToEdit?: string;
}) {
  const { values, setFieldValue, setValues } = useFormikContext<ICampaignFormState>();
  const fieldsDisabled = lockedFields.includes(fieldToEdit);
  const customFieldDisabled = values.lockedFields?.includes(fieldToEdit);
  const { allowDropdownLocation } = useFlags<IFeatureToggles>();

  let fieldValues = {} as ICampaignFormField;
  if (action === "edit") {
    fieldValues = values.formFields[fieldToEdit] as ICampaignFormField;
    fieldValues.isSearchable = fieldValues.isSearchable ?? true;
  } else {
    //initialize new object for adding
    fieldValues = {
      enabled: true,
      required: false,
      fieldName: "",
      label: "",
      fieldType: "text",
      fieldTypeLabel: "Text Input",
      textLength: "0",
      dropdownOptions: [],
      isSearchable: true,
      archived: false,
      adGroupOptions: [],
    };
  }

  const isAdd = action === "add";
  const initialState = { ...fieldValues } as ICampaignFormField;

  //Note: This could be refactored to use formik.setFieldErrors / managed by formik,
  //but since formik is everywhere on the form it can cause unintended re-renders
  const formErrorInit = { fieldLabel: "", fieldType: "", maxLength: "", dropdownOptions: "" };

  const [open, setOpen] = useState(false);
  const [presetModalOpen, setPresetModalOpen] = useState<boolean>(false);
  const [dropdownOptions, setDropdownOptions] = useState<string[]>(fieldValues.dropdownOptions);
  const [fieldType, setFieldType] = useState(fieldValues.fieldType);
  const [maxLength, setMaxLength] = useState(fieldValues.textLength);
  const [fieldLabel, setFieldLabel] = useState(fieldValues.label);
  const [fieldSearchable, setFieldSearchable] = useState(fieldValues.isSearchable ?? true);
  const [optionValue, setOptionValue] = useState("");
  const [formErrors, setFormErrors] = useState(formErrorInit);

  const labelHistory = values.labelHistory[fieldToEdit] ?? [];

  function resetState() {
    //Can refactor state to exist in a single object and leverage useReducer / setter function for state object to do this in one line
    setDropdownOptions(initialState.dropdownOptions);
    setFieldType(initialState.fieldType);
    setFieldSearchable(initialState.isSearchable ?? true);
    setFieldLabel(initialState.label);
    setMaxLength(initialState.textLength);
    setOptionValue("");
    setFormErrors(formErrorInit);
  }

  function handleClose(event: any, reason: "backdropClick" | "escapeKeyDown" = "escapeKeyDown") {
    if (reason === "backdropClick") {
      return;
    }
    resetState();
    setOpen(false);
  }

  function saveEditedField() {
    const updatedFieldValue = {
      enabled: values.formFields[fieldToEdit].enabled,
      required: values.formFields[fieldToEdit].required,
      fieldName: values.formFields[fieldToEdit].fieldName,
      fieldType: fieldType,
      fieldTypeLabel: fieldOptions[fieldType],
      label: fieldLabel.trim(),
      dropdownOptions: dropdownOptions,
      textLength: fieldType === "text" ? maxLength : "0",
      order: values.formFields[fieldToEdit].order,
      isSearchable: fieldSearchable ?? true,
      archived: values.formFields[fieldToEdit]?.archived ?? false,
      adGroupOptions: _.cloneDeep(values.formFields[fieldToEdit].adGroupOptions),
    } as ICampaignFormField;

    setValues({
      ...values,
      formFields: {
        ...values.formFields,
        [fieldToEdit]: updatedFieldValue as BaseFormField,
      },
    });
  }

  function addCustomField() {
    fieldToEdit = `customField${values.totalCustomFields}`;
    setFieldValue("totalCustomFields", values.totalCustomFields + 1);
    setFieldValue(`formFields.${fieldToEdit}`, {
      enabled: true,
      required: false,
      fieldName: `Custom Field ${values.totalCustomFields + 1}`,
      fieldType: fieldType,
      fieldTypeLabel: fieldOptions[fieldType],
      label: fieldLabel.trim(),
      dropdownOptions: dropdownOptions,
      textLength: fieldType === "text" ? maxLength : "0",
      order: Object.keys(values.formFields).length,
      isSearchable: fieldSearchable ?? true,
      archived: false,
    });
    resetState();
  }

  async function saveChanges() {
    setFormErrors(formErrorInit);
    await FieldModalSchema.validate(
      { fieldType, fieldLabel, maxLength, dropdownOptions },
      { abortEarly: false, context: { editedField: fieldToEdit, fields: values.formFields } },
    )
      .then(() => {
        if (isAdd) {
          addCustomField();
        } else {
          saveEditedField();
        }
        setOptionValue("");
        setOpen(false);
      })
      .catch((e) => {
        e.inner.forEach((error: { path: string; message: string }) => {
          setFormErrors((prevState) => ({
            ...prevState,
            [error.path]: error.message,
          }));
        });
      });
  }

  function inputDropdownOption(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (formErrors["dropdownOptions"]) {
      setFormErrors((prevState) => ({
        ...prevState,
        dropdownOptions: "",
      }));
    }
    setOptionValue(event.target.value);
  }

  function addListItemOnEnter(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter") {
      addListItem();
    }
  }

  function addListItem() {
    if (optionValue.trim() === "") {
      setFormErrors((prevState) => ({
        ...prevState,
        dropdownOptions: "Dropdown option cannot be empty.",
      }));
    } else if (dropdownOptions.includes(optionValue.trim())) {
      setFormErrors((prevState) => ({
        ...prevState,
        dropdownOptions: "Dropdown option already exists.",
      }));
    } else {
      setDropdownOptions([...dropdownOptions, optionValue.trim()]);
      setOptionValue("");
    }
  }

  function removeListItem(value: string) {
    setDropdownOptions(dropdownOptions.filter((item) => value !== item));
  }

  function buildOpenButton() {
    if (isAdd) {
      return (
        <Stack direction="row" spacing={2}>
          <Button
            aria-label="Create Field"
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            id="add-custom-field-button"
          >
            Add Custom Field
          </Button>
          <Button
            aria-label="Add preset Field"
            variant="contained"
            startIcon={<Add />}
            onClick={() => setPresetModalOpen(true)}
            id="add-preset-field-button"
            data-test="add-preset-field-button"
          >
            Add Preset Field
          </Button>
        </Stack>
      );
    } else {
      return (
        <IconButton
          data-test="edit-field-button"
          aria-label={`edit field ${values.formFields[fieldToEdit].fieldName}`}
          sx={{ border: "1px solid", borderRadius: ".2em" }}
          onClick={() => setOpen(true)}
        >
          <Edit />
        </IconButton>
      );
    }
  }

  const addPresetField = (val: any) => {
    const fieldToEdit = `customField${values.totalCustomFields}`;
    setFieldValue("totalCustomFields", values.totalCustomFields + 1);
    setFieldValue(`formFields.${fieldToEdit}`, {
      enabled: true,
      required: false,
      fieldName: `Custom Field ${values.totalCustomFields + 1}`,
      fieldType: val.fieldType,
      fieldTypeLabel: fieldOptions[val.fieldType],
      label: val.fieldLabel.trim(),
      dropdownOptions: val.dropdownOptions,
      textLength: val.fieldType === "text" ? maxLength : "0",
      order: Object.keys(values.formFields).length,
      isSearchable: val.fieldSearchable ?? true,
      archived: false,
    });
    resetState();
  };

  return (
    <>
      {buildOpenButton()}
      <Modal
        title={isAdd ? "Add Field" : "Edit Field"}
        isOpen={open}
        handleCloseModal={handleClose}
        dataTestId="field-edit"
        size="md"
        content={
          <>
            <AddEditField
              values={values}
              fieldToEdit={fieldToEdit}
              isAdd={isAdd}
              saveChanges={saveChanges}
              fieldLabel={fieldLabel}
              setFieldLabel={setFieldLabel}
              formErrors={formErrors}
              fieldsDisabled={fieldsDisabled}
              customFieldDisabled={customFieldDisabled}
              fieldType={fieldType}
              setFieldType={setFieldType}
              allowDropdownLocation={allowDropdownLocation}
            />

            {(fieldType === "dropdown" || fieldType === "multiSelect") && (
              <EditDropdownFieldDetails
                optionValue={optionValue}
                setDropdownOptions={setDropdownOptions}
                inputDropdownOption={inputDropdownOption}
                addListItemOnEnter={addListItemOnEnter}
                addListItem={addListItem}
                removeListItem={removeListItem}
                formErrors={formErrors}
                fieldType={fieldType}
                setFieldType={setFieldType}
                fieldToEdit={fieldToEdit}
                fieldSearchable={fieldSearchable}
                setFieldSearchable={setFieldSearchable}
                dropdownOptions={dropdownOptions}
              />
            )}
            {fieldType === "text" && fieldToEdit !== "phone" && (
              <EditTextFieldDetails
                maxLength={maxLength}
                setMaxLength={setMaxLength}
                fieldsDisabled={fieldsDisabled}
                formErrors={formErrors}
              />
            )}
            {fieldType === "interestPicker" && (
              <JobAlertsAdGroupDisplay
                onChange={() => setDropdownOptions(fieldValues.dropdownOptions)}
                field={fieldValues}
              />
            )}
            <DisplayLabelHistory currentLabel={fieldLabel} labelHistory={labelHistory} />
          </>
        }
        footerContent={<></>}
      />
      <Modal
        isOpen={presetModalOpen}
        handleCloseModal={() => setPresetModalOpen(false)}
        content={
          <List>
            {presetFields.map((c, idx) => (
              <ListItem
                sx={{ border: "1px solid #ccc", borderRadius: 1, marginBottom: 1 }}
                disablePadding
                key={`preset-field-${idx}`}
                data-test={`preset-field-${idx}`}
              >
                <ListItemButton>
                  <ListItemText
                    primary={<Typography variant="subtitle1">{c.title}</Typography>}
                    secondary={
                      <Typography fontSize={12} color="grey">
                        {c.description}
                      </Typography>
                    }
                    onClick={() => {
                      const exists = Object.values(values.formFields).filter((d) =>
                        d.label.includes(c.title),
                      );
                      addPresetField({
                        fieldType: c.fieldType,
                        fieldLabel: exists.length <= 0 ? c.title : `${c.title} (${exists.length})`,
                        fieldSearchable: c.searchable,
                        dropdownOptions: c.dropdownOptions,
                      });
                      setPresetModalOpen(false);
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        }
        title="Add Preset Field"
        dataTestId="preset-field"
        footerContent={<></>}
        size="sm"
      />
    </>
  );
}

const AddEditField = ({
  values,
  fieldToEdit,
  isAdd,
  saveChanges,
  fieldLabel,
  setFieldLabel,
  formErrors,
  fieldsDisabled,
  customFieldDisabled,
  fieldType,
  setFieldType,
  allowDropdownLocation,
}: {
  values: ICampaignFormState;
  fieldToEdit: string;
  isAdd: boolean;
  saveChanges: () => void;
  fieldLabel: string;
  setFieldLabel: (value: React.SetStateAction<string>) => void;
  formErrors: { [key: string]: string };
  fieldsDisabled: boolean;
  customFieldDisabled?: boolean;
  fieldType: string;
  setFieldType: (value: React.SetStateAction<string>) => void;
  allowDropdownLocation: boolean;
}) => {
  return (
    <Box sx={{ padding: "0px 10px 0px 10px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <h5 style={{ marginBottom: "0px" }}>Field Name:</h5>
          <h2 style={{ marginTop: "5px" }} id="field-name-header">
            {values?.formFields[fieldToEdit]?.fieldName || "Custom Field"}
          </h2>
        </Box>
        <Button
          aria-label={isAdd ? "Add Field" : "Save Changes"}
          sx={{ height: "50%" }}
          variant="contained"
          startIcon={isAdd ? <Add /> : <Save />}
          onClick={() => saveChanges()}
          id="add-edit-field-save"
          disabled={fieldLabel.length > maxFieldLabelLength}
        >
          {isAdd ? "Add Field" : "Save Changes"}
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "50%",
            flexGrow: 1,
            paddingRight: "5px",
            paddingBottom: "10px",
          }}
        >
          <FormLabel htmlFor="fieldLabel">Field Label</FormLabel>
          <TextField
            id="fieldLabel"
            value={fieldLabel}
            onChange={(event) => setFieldLabel(event.target.value)}
            error={fieldLabel.length > maxFieldLabelLength}
            helperText={`${fieldLabel.length}/${maxFieldLabelLength}`}
          />
          {<FormError fieldName={"fieldLabel"} errors={formErrors} id="field-label-error" />}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "50%",
            flexGrow: 1,
            paddingLeft: "5px",
          }}
        >
          <FormLabel htmlFor="fieldType">Field Type</FormLabel>
          <Select
            name="fieldType"
            value={fieldType}
            disabled={fieldsDisabled || customFieldDisabled}
            endAdornment={
              fieldsDisabled || customFieldDisabled ? (
                <Lock sx={{ mr: "15px" }} data-test="lock-icon-field-type" />
              ) : undefined
            }
            onChange={(event) => setFieldType(event.target.value)}
            data-test="edit-field-type-dropdown"
          >
            {fieldType === "interestPicker" && <MenuItem value={"interestPicker"}>Interest Picker</MenuItem>}
            {(fieldToEdit !== "location" || allowDropdownLocation) && (
              <MenuItem value={"dropdown"}>Dropdown</MenuItem>
            )}
            <MenuItem sx={{ display: "none" }} value={"multiSelect"}>
              Dropdown
            </MenuItem>
            <MenuItem value={"text"}>Text Input</MenuItem>
            {fieldToEdit !== "location" && <MenuItem value={"upload"}>File Upload</MenuItem>}
            {fieldType === "email" && <MenuItem value={"email"}>Email Input</MenuItem>}
            {fieldType === "toggle" && <MenuItem value={"toggle"}>Toggle</MenuItem>}
            {fieldType === "phone" && <MenuItem value={"phone"}>Phone Input</MenuItem>}
          </Select>
          {isAdd && (
            <FormHelperText>Note: Field type cannot be changed after it has been created.</FormHelperText>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const EditTextFieldDetails = ({
  maxLength,
  setMaxLength,
  fieldsDisabled,
  formErrors,
}: {
  maxLength: string;
  setMaxLength: (value: React.SetStateAction<string>) => void;
  fieldsDisabled: boolean;
  formErrors: { [key: string]: string };
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "50%",
        flexGrow: 1,
        padding: "0px 10px 0px 10px",
      }}
    >
      <Divider sx={{ mb: "10px" }} />
      <FormLabel htmlFor="maxTextLength">Max Text Length (Characters)</FormLabel>
      <TextField
        id="maxTextLength"
        disabled={fieldsDisabled}
        value={maxLength}
        onChange={(event: any) => {
          if (/^\d+$/.test(event.target.value) || event.target.value === "") {
            setMaxLength(event.target.value);
          }
        }}
      ></TextField>
      {formErrors["maxLength"] && (
        <FormError fieldName={"maxLength"} errors={formErrors} id="text-length-error" />
      )}
    </Box>
  );
};

export const DisplayLabelHistory = ({
  currentLabel,
  labelHistory,
}: {
  currentLabel: string;
  labelHistory: Set<string>;
}) => {
  const labelArray = useMemo(
    () =>
      Array.from(labelHistory)
        .filter((label) => label !== currentLabel)
        .sort(),
    [labelHistory, currentLabel],
  );
  return labelArray.length > 0 ? (
    <Accordion
      variant="outlined"
      sx={{
        mt: 2,
        padding: 0,
        minHeight: 0,
        borderInline: 0,
        borderBottom: 0,
        "& .MuiAccordionSummary-root, & .Mui-expanded": {
          minHeight: 0,
        },
        "& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content": {
          marginBlock: 1,
        },
      }}
    >
      <AccordionSummary expandIcon={<ArrowDropDown />} sx={{ my: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "light", m: 0 }}>
          Previous Field Labels ({labelArray.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <List
          sx={{
            maxHeight: "250px",
            overflow: "auto",
            py: 0,
            pr: 1,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {labelArray.map((label, index) => (
            <ListItem
              key={index}
              component={Chip}
              label={label}
              sx={{ p: 0, m: 0, width: "auto", borderRadius: 0 }}
              size="small"
            />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  ) : null;
};

const EditDropdownFieldDetails = ({
  optionValue,
  setDropdownOptions,
  inputDropdownOption,
  addListItemOnEnter,
  addListItem,
  removeListItem,
  formErrors,
  fieldType,
  setFieldType,
  fieldToEdit,
  fieldSearchable,
  setFieldSearchable,
  dropdownOptions,
}: {
  optionValue: string;
  setDropdownOptions: (value: React.SetStateAction<string[]>) => void;
  inputDropdownOption: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  addListItemOnEnter: (event: KeyboardEvent<HTMLDivElement>) => void;
  addListItem: () => void;
  removeListItem: (value: string) => void;
  formErrors: { [key: string]: string };
  fieldType: string;
  setFieldType: (value: React.SetStateAction<string>) => void;
  fieldToEdit: string;
  fieldSearchable: boolean;
  setFieldSearchable: (value: React.SetStateAction<boolean>) => void;
  dropdownOptions: string[];
}) => {
  const [page, setPage] = useState<number>(0);
  const pageSize = 100;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        padding: "0px 10px 0px 10px",
      }}
    >
      <Divider sx={{ mb: "10px" }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "50%",
          flexGrow: 1,
          paddingRight: "5px",
          paddingBottom: "10px",
        }}
      >
        <FormLabel htmlFor="addDropdown">Add Dropdown Options</FormLabel>
        <TextField
          id="add-dropdown-option"
          name="addDropdown"
          value={optionValue}
          error={formErrors["dropdownOptions"] ? true : false}
          onChange={inputDropdownOption}
          onKeyPress={addListItemOnEnter}
          InputProps={{
            endAdornment: (
              <IconButton id="add-dropdown-option-button" aria-label="add option" onClick={addListItem}>
                <Add />
              </IconButton>
            ),
          }}
        />
        {formErrors["dropdownOptions"] && (
          <FormError fieldName={"dropdownOptions"} errors={formErrors} id="dropdown-error" />
        )}

        <Box>
          <AllowMultiSelectCheckbox
            checked={fieldType === "multiSelect"}
            onClick={() => setFieldType(fieldType === "dropdown" ? "multiSelect" : "dropdown")}
            disabled={fieldToEdit === "location"}
          />
          <EnableSearchFilterCheckbox
            checked={fieldSearchable}
            onClick={() => setFieldSearchable(!fieldSearchable)}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "50%",
          flexGrow: 1,
          paddingRight: "5px",
          paddingBottom: "10px",
        }}
      >
        <FormLabel htmlFor="currentDropdown">Current Dropdown Options</FormLabel>
        {dropdownOptions.length > pageSize && (
          <TablePagination
            component="div"
            count={dropdownOptions.length}
            page={page}
            onPageChange={(_ev, p) => setPage(p)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
          />
        )}
        <DraggableDropdownOptions
          dropdownOptions={dropdownOptions.slice(page * pageSize, page * pageSize + pageSize)}
          removeListItem={removeListItem}
          setDropdownList={setDropdownOptions}
          disableOrder={dropdownOptions.length > pageSize}
        />
      </Box>
    </Box>
  );
};

const AllowMultiSelectCheckbox = ({
  checked,
  onClick,
  disabled,
}: {
  checked: boolean;
  onClick: () => void;
  disabled: boolean;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <FormControlLabel
        disableTypography
        labelPlacement="start"
        label={
          <FormLabel
            id="multi-select-label"
            component={"span"}
            sx={{
              width: 1,
              display: "flex",
              alignItems: "center",
              gap: "0.5ch",
            }}
          >
            Allow Multiple Selections
          </FormLabel>
        }
        control={
          <Checkbox
            disabled={disabled}
            inputProps={{ "aria-labelledby": "multi-select-label" }}
            id="set-multi-select"
            checked={checked}
            onClick={onClick}
          />
        }
        sx={{
          width: 1,
          display: "flex",
          alignItems: "center",
          mr: 0,
          ml: 0,
        }}
      />
    </Box>
  );
};

const EnableSearchFilterCheckbox = ({ checked, onClick }: { checked: boolean; onClick: () => void }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Tooltip
        arrow
        title="Enables type to search and filter dropdown options. Note: may negatively impact mobile users."
      >
        <FormControlLabel
          disableTypography
          labelPlacement="start"
          label={
            <FormLabel
              id="searchable-label"
              component={"span"}
              sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                gap: "0.5ch",
              }}
            >
              Enable Search Filter&nbsp;
              <Info fontSize="small" color="disabled" />
            </FormLabel>
          }
          control={
            <Checkbox
              id="set-searchable"
              checked={checked}
              onClick={onClick}
              inputProps={{ "aria-labelledby": "searchable-label" }}
            />
          }
          sx={{
            width: 1,
            display: "flex",
            alignItems: "center",
            mr: 0,
            ml: 0,
          }}
        />
      </Tooltip>
    </Box>
  );
};
